import React, { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase, getAllTags } from '@/lib/supabase';
import Navbar from '@/components/Navbar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, X, Plus } from 'lucide-react';
import { useDebounce } from '@/hooks/use-debounce';
import { useAuth } from '@/contexts/AuthContext';
import PathPreview from '@/components/PathPreview';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';

interface GalleryCollection {
  id: string;
  name: string;
  description: string | null;
  public_id: string;
  created_at: string;
  user_id: string;
  tags: string[];
  videos: {
    thumbnail: string;
    title: string;
  }[];
}

const ITEMS_PER_PAGE = 12;

const Gallery = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const debouncedSearch = useDebounce(searchQuery, 300);

  const { data: tags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: getAllTags,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('title');
      if (error) throw error;
      return data;
    },
  });

  const { data: paths = [] } = useQuery({
    queryKey: ['public-paths'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('paths')
        .select('*')
        .eq('is_public', true)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const fetchCollections = async ({ pageParam = 0 }) => {
    const from = pageParam * ITEMS_PER_PAGE;
    const to = from + ITEMS_PER_PAGE - 1;

    let query = supabase
      .from('collections')
      .select(`
        *,
        videos:collection_videos(thumbnail, title)
      `)
      .eq('is_public', true)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (debouncedSearch) {
      query = query.ilike('name', `%${debouncedSearch}%`);
    }

    if (selectedTag) {
      query = query.contains('tags', [selectedTag]);
    }

    const { data, error } = await query;
    if (error) throw error;

    return {
      collections: data as GalleryCollection[],
      nextPage: data.length === ITEMS_PER_PAGE ? pageParam + 1 : undefined,
    };
  };

  const {
    data: collectionsPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['public-collections', debouncedSearch, selectedTag],
    queryFn: fetchCollections,
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const handlePathClick = (pathId: string) => {
    navigate(`/paths/${pathId}`);
  };

  const handleCollectionClick = (publicId: string) => {
    navigate(`/collection/${publicId}`);
  };

  const handleTagClick = (tag: string) => {
    setSelectedTag(tag === selectedTag ? null : tag);
  };

  const handleCategoryClick = (slug: string) => {
    navigate(`/category/${slug}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto mb-8">
          <div className="flex items-center justify-between mb-4">
            <form onSubmit={handleSearch} className="relative flex-1">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search collections..."
                className="w-full pl-4 pr-12 h-12 text-lg bg-secondary/50"
              />
              <Button
                type="submit"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2"
                variant="ghost"
              >
                <Search size={20} />
              </Button>
            </form>
            {user && (
              <Button onClick={() => navigate('/manage')} className="ml-4">
                <Plus size={16} className="mr-2" />
                Create
              </Button>
            )}
          </div>

          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <Button
                  key={tag}
                  variant={selectedTag === tag ? 'default' : 'secondary'}
                  size="sm"
                  onClick={() => handleTagClick(tag)}
                  className="rounded-full"
                >
                  {tag}
                  {selectedTag === tag && (
                    <X size={14} className="ml-1" onClick={() => setSelectedTag(null)} />
                  )}
                </Button>
              ))}
            </div>
          )}
        </div>

        {categories.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Categories</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {categories.map((category) => (
                <Card
                  key={category.id}
                  className="p-4 cursor-pointer hover:bg-accent/10 transition-colors"
                  onClick={() => handleCategoryClick(category.slug)}
                >
                  <h3 className="font-medium text-sm mb-1">{category.title}</h3>
                  {category.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {category.description}
                    </p>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}

        {paths.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">Learning Paths</h2>
            <ScrollArea className="w-full whitespace-nowrap rounded-md">
              <div className="flex space-x-4 pb-4">
                {paths.map((path) => (
                  <div key={path.id} className="w-[400px] flex-none">
                    <PathPreview
                      pathId={path.id}
                      title={path.title}
                      onClick={() => handlePathClick(path.id)}
                    />
                  </div>
                ))}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="bg-card rounded-lg h-64 animate-pulse border border-border"
              />
            ))}
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-bold mb-6">Collections</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {collectionsPages?.pages.map((page) =>
                page.collections.map((collection) => (
                  <div
                    key={collection.id}
                    className="group bg-card border border-border rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-lg"
                    onClick={() => handleCollectionClick(collection.public_id)}
                  >
                    <div className="aspect-video relative">
                      <div className="grid grid-cols-2 gap-0.5 absolute inset-0">
                        {collection.videos.slice(0, 4).map((video, idx) => (
                          <div key={idx} className="overflow-hidden">
                            <img
                              src={video.thumbnail}
                              alt={video.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        ))}
                      </div>
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Button variant="secondary">View Collection</Button>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-medium text-lg mb-1 line-clamp-1">{collection.name}</h3>
                      {collection.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                          {collection.description}
                        </p>
                      )}
                      {collection.tags && collection.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {collection.tags.map((tag) => (
                            <span
                              key={tag}
                              className="text-xs bg-accent/10 text-accent-foreground px-2 py-0.5 rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {hasNextPage && (
              <div className="mt-8 text-center">
                <Button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  variant="outline"
                  size="lg"
                >
                  {isFetchingNextPage ? 'Loading...' : 'Load More'}
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Gallery;